import { useState, useEffect } from 'react'
import { RefreshCw, Save, AlertCircle, CheckCircle, X, Plus, ExternalLink, History, RotateCcw } from 'lucide-react'
import {
  STAGE_TRANSITIONS,
  UNIVERSAL_TERMINALS,
  DECLINED_FROM,
  TERMINAL_STAGES,
  STAGE_SLA,
  STAGE_DISPLAY,
  ALL_STAGES,
  fetchStageConfig,
} from '../constants/applicationStages'
import { authenticatedFetch } from '../api/authApi'

const API_BASE = import.meta.env.VITE_API_URL || ''
const NODE_RED_URL = import.meta.env.VITE_NODE_RED_URL || ''

interface StageConfig {
  version?: number
  label?: string
  transitions: Record<string, string[]>
  universalTerminals: string[]
  declinedFrom: string[]
  terminalStages: string[]
  sla: Record<string, number>
  source?: string
}

interface ConfigVersion {
  version: number
  label: string
  is_active: boolean
  created_by: string | null
  created_at: string | null
}

interface WorkflowConfigEditorProps {
  token: string
}

export default function WorkflowConfigEditor({ token }: WorkflowConfigEditorProps) {
  const [config, setConfig] = useState<StageConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [versions, setVersions] = useState<ConfigVersion[]>([])
  const [showVersions, setShowVersions] = useState(false)
  const [activatingVersion, setActivatingVersion] = useState<number | null>(null)
  const [addingTransitionFor, setAddingTransitionFor] = useState<string | null>(null)

  const loadConfig = async () => {
    setLoading(true)
    setMessage(null)
    try {
      const resp = await authenticatedFetch(`${API_BASE}/api/applications/stage-config`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error('Failed to load config')
      const data = await resp.json()
      setConfig(data)
    } catch {
      setConfig({
        transitions: { ...STAGE_TRANSITIONS },
        universalTerminals: [...UNIVERSAL_TERMINALS],
        declinedFrom: [...DECLINED_FROM],
        terminalStages: [...TERMINAL_STAGES],
        sla: { ...STAGE_SLA },
        source: 'local-fallback',
      })
    } finally {
      setLoading(false)
    }
  }

  const loadVersions = async () => {
    try {
      const resp = await authenticatedFetch(`${API_BASE}/api/applications/stage-config/versions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: resp.statusText }))
        console.error('Failed to load versions:', resp.status, err.detail || err)
        setMessage({ type: 'error', text: `Failed to load versions: ${err.detail || resp.statusText}` })
        return
      }
      const data = await resp.json()
      setVersions(data.versions || [])
    } catch (e) {
      console.error('Failed to load versions:', e)
    }
  }

  useEffect(() => {
    loadConfig()
  }, [token])

  const handleSave = async () => {
    if (!config) return
    const label = prompt('Version label (optional):', '')
    if (label === null) return // cancelled

    setSaving(true)
    setMessage(null)
    try {
      const resp = await authenticatedFetch(`${API_BASE}/api/applications/stage-config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...config, label: label || undefined }),
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ detail: 'Unknown error' }))
        throw new Error(err.detail || 'Save failed')
      }
      const updated = await resp.json()
      setConfig(updated)
      await fetchStageConfig(token)
      setMessage({ type: 'success', text: `Config saved (v${updated.version}${updated.label ? ' — ' + updated.label : ''})` })
      if (showVersions) loadVersions()
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Save failed' })
    } finally {
      setSaving(false)
    }
  }

  const handleActivateVersion = async (version: number) => {
    setActivatingVersion(version)
    try {
      const resp = await authenticatedFetch(`${API_BASE}/api/applications/stage-config/versions/${version}/activate`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!resp.ok) throw new Error('Activation failed')
      const activated = await resp.json()
      setConfig(activated)
      await fetchStageConfig(token)
      setMessage({ type: 'success', text: `Rolled back to v${version}` })
      loadVersions()
    } catch (e: any) {
      setMessage({ type: 'error', text: e.message || 'Rollback failed' })
    } finally {
      setActivatingVersion(null)
    }
  }

  const handleSlaChange = (stage: string, days: string) => {
    if (!config) return
    const num = parseInt(days, 10)
    const newSla = { ...config.sla }
    if (isNaN(num) || num <= 0) {
      delete newSla[stage]
    } else {
      newSla[stage] = num
    }
    setConfig({ ...config, sla: newSla })
  }

  const handleRemoveTransition = (stage: string, target: string) => {
    if (!config) return
    const current = config.transitions[stage] || []
    setConfig({
      ...config,
      transitions: {
        ...config.transitions,
        [stage]: current.filter((s) => s !== target),
      },
    })
  }

  const handleAddTransition = (stage: string, target: string) => {
    if (!config) return
    const current = config.transitions[stage] || []
    if (current.includes(target)) return
    setConfig({
      ...config,
      transitions: {
        ...config.transitions,
        [stage]: [...current, target],
      },
    })
    setAddingTransitionFor(null)
  }

  if (!config) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
        <div className="text-sm text-gray-500">
          {loading ? 'Loading workflow config...' : 'Config unavailable'}
        </div>
      </div>
    )
  }

  const stages = Object.keys(config.transitions)

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">
          Workflow Stage Config
          {config.version != null ? (
            <span className="text-sm font-normal text-gray-500 ml-2">
              v{config.version}{config.label ? ` — ${config.label}` : ''}
            </span>
          ) : null}
          {config.source === 'fallback' || config.source === 'local-fallback' ? (
            <span className="text-xs font-normal text-amber-600 ml-2">(fallback)</span>
          ) : null}
        </h2>
        <div className="flex items-center gap-2">
          {NODE_RED_URL && import.meta.env.DEV && (
            <a
              href={NODE_RED_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              title="Node-RED flow editor (local dev only)"
            >
              <ExternalLink className="w-4 h-4" /> Node-RED
            </a>
          )}
          <button
            onClick={() => { setShowVersions(!showVersions); if (!showVersions) loadVersions() }}
            className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
          >
            <History className="w-4 h-4" /> Versions
          </button>
          <button
            onClick={loadConfig}
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save New Version'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      {/* Version History Panel */}
      {showVersions && (
        <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <h3 className="text-sm font-medium text-purple-800 mb-3">Version History</h3>
          {versions.length === 0 ? (
            <p className="text-xs text-purple-600">No versions found</p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {versions.map((v) => (
                <div
                  key={v.version}
                  className={`flex items-center justify-between p-2 rounded ${
                    v.is_active ? 'bg-purple-100 border border-purple-300' : 'bg-white border border-purple-100'
                  }`}
                >
                  <div>
                    <span className="text-sm font-medium text-purple-900">v{v.version}</span>
                    <span className="text-xs text-purple-600 ml-2">{v.label}</span>
                    {v.is_active && (
                      <span className="ml-2 px-1.5 py-0.5 text-xs rounded bg-purple-600 text-white">Active</span>
                    )}
                    {v.created_at && (
                      <span className="text-xs text-purple-400 ml-2">
                        {new Date(v.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {!v.is_active && (
                    <button
                      onClick={() => handleActivateVersion(v.version)}
                      disabled={activatingVersion === v.version}
                      className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 disabled:opacity-50"
                    >
                      <RotateCcw className="w-3 h-3" />
                      {activatingVersion === v.version ? 'Activating...' : 'Rollback'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stage Transitions Table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 font-medium text-gray-700">Stage</th>
              <th className="text-left py-2 px-3 font-medium text-gray-700">Allowed Next Stages</th>
              <th className="text-left py-2 px-3 font-medium text-gray-700 w-20">SLA (days)</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((stage) => {
              const currentTransitions = config.transitions[stage] || []
              const availableToAdd = ALL_STAGES.filter(
                (s) => s !== stage && !currentTransitions.includes(s)
              )
              return (
                <tr key={stage} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 px-3">
                    <span className="font-medium text-gray-900">{STAGE_DISPLAY[stage] || stage}</span>
                    <br />
                    <span className="text-xs text-gray-400">{stage}</span>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap gap-1 items-center">
                      {currentTransitions.map((next) => (
                        <span
                          key={next}
                          className="inline-flex items-center gap-0.5 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700"
                        >
                          {STAGE_DISPLAY[next] || next}
                          <button
                            onClick={() => handleRemoveTransition(stage, next)}
                            className="ml-0.5 hover:text-red-600"
                            title={`Remove ${next}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      {currentTransitions.length === 0 && (
                        <span className="text-xs text-gray-400 italic">Terminal</span>
                      )}
                      {addingTransitionFor === stage ? (
                        <select
                          autoFocus
                          className="text-xs border border-blue-300 rounded px-1 py-0.5"
                          onChange={(e) => {
                            if (e.target.value) handleAddTransition(stage, e.target.value)
                          }}
                          onBlur={() => setAddingTransitionFor(null)}
                          defaultValue=""
                        >
                          <option value="" disabled>Select stage...</option>
                          {availableToAdd.map((s) => (
                            <option key={s} value={s}>{STAGE_DISPLAY[s] || s}</option>
                          ))}
                        </select>
                      ) : (
                        <button
                          onClick={() => setAddingTransitionFor(stage)}
                          className="inline-flex items-center px-1.5 py-0.5 text-xs rounded-full border border-dashed border-blue-300 text-blue-500 hover:bg-blue-50"
                          title="Add transition"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="number"
                      min="0"
                      value={config.sla[stage] || ''}
                      onChange={(e) => handleSlaChange(stage, e.target.value)}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="-"
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Special Rules */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <h3 className="text-sm font-medium text-amber-800 mb-2">Universal Terminals</h3>
          <p className="text-xs text-amber-600 mb-2">Any active stage can transition to these</p>
          <div className="flex flex-wrap gap-1">
            {config.universalTerminals.map((s) => (
              <span key={s} className="inline-block px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                {STAGE_DISPLAY[s] || s}
              </span>
            ))}
          </div>
        </div>
        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
          <h3 className="text-sm font-medium text-red-800 mb-2">Declined From</h3>
          <p className="text-xs text-red-600 mb-2">Only these stages can transition to "declined"</p>
          <div className="flex flex-wrap gap-1">
            {config.declinedFrom.map((s) => (
              <span key={s} className="inline-block px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                {STAGE_DISPLAY[s] || s}
              </span>
            ))}
          </div>
        </div>
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-800 mb-2">Terminal Stages</h3>
          <p className="text-xs text-gray-600 mb-2">No further transitions allowed</p>
          <div className="flex flex-wrap gap-1">
            {config.terminalStages.map((s) => (
              <span key={s} className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">
                {STAGE_DISPLAY[s] || s}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-400">
        {stages.length} stages configured
      </div>
    </div>
  )
}
